(ns frontend.view.modals.pipeline-edit.view
  (:require
   [clojure.string :as string]
   [common.entity :as entity]
   [common.localization :as loc]
   [common.schema.assets :as assets]
   [common.schema.constraints :as constraints]
   [common.schema.option-filters :as option-filters]
   [common.schema.registry :as registry]
   [common.util :as cu]
   [frontend.logging :as logging]
   [frontend.state.current-session :as session]
   [frontend.utils :as fu]
   [frontend.view.components.clipboard :as clipboard]
   [frontend.view.modals.components.processing-modal :as processing-modal]
   [frontend.view.modals.pipeline-edit.events :as events]
   [ui.components.modal.view :as modal]
   [ui.components.radix-ui.tooltip.view :as tooltip]
   [ui.components.toggle.view :as toggle]
   [ui.editors.labelled-input.view :as labelled-input]))

(defonce modal-key :pipeline-edit-modal)

(defn not-unique-pipeline?
  [existing-pipelines pipeline-name]
  (let [lc (fu/trimmed-lower-case pipeline-name)]
    (contains? (->> existing-pipelines
                    (mapv :pipeline/name)
                    (mapv fu/trimmed-lower-case)
                    (set))
               lc)))

(defn sales-pipeline-target-constraint
  [state]
  (let [attribute :pipeline/target-asset-types
        tenant-schema @session/tenant-schema
        field-schema (registry/get-field-schema tenant-schema attribute)
        entity @state
        new-value (get entity attribute)
        field-constraints (:field/constraints field-schema)
        errors (constraints/validate field-constraints
                                     (:db/id entity)
                                     new-value
                                     entity
                                     nil
                                     loc/tr
                                     js/console.log)]
    errors))

(def types-with-funds
  #{:entity.type/oppo
    :entity.type/fundraising-opportunity})

(defn entity-options
  [entities]
  (->> entities
       (remove :common/disabled?)
       (mapv (fn [e]
               {:value (:db/id e)
                :label (entity/display-string e)}))))

(defn ref-multi-value
  [v]
  (->> v
       (mapv (fn [item]
               (if (map? item)
                 (:db/id item)
                 item)))))

(defn view-body
  [state existing-pipelines target-asset-label]
  (let [tenant-schema @session/tenant-schema
        pipeline-teams-schema (registry/get-field-schema tenant-schema :pipeline/teams)
        pipeline-entity-type (:pipeline/entity-type @state)
        pipeline-name (:pipeline/name @state)
        funds? (and (session/has-funds-access?)
                    (contains? types-with-funds pipeline-entity-type))]
    [:div
     {:class "flex flex-col gap-4"}
     [:div
      [labelled-input/view
       {:label       (loc/tr :pipeline/name)
        :mandatory?  true
        :placeholder (loc/tr :add- [(string/lower-case (loc/tr :pipeline/name))])
        :data-type   :data.type/text-simple
        :value       pipeline-name
        :on-change   (fn [new-value]
                       (swap! state assoc :pipeline/name new-value))
        :on-blur     (fn [new-value]
                       (let [err (cond (not-unique-pipeline? existing-pipelines new-value)
                                       (loc/tr :this-pipeline-already-exists)
                                       (string/blank? new-value)
                                       (loc/tr :error-field-not-clearable [(loc/tr :pipeline/name)])
                                       :else
                                       nil)]
                         (swap! state assoc :error-message err)))}]
      [:p {:class "text-xs text-danger-light-text"}
       (:error-message @state)]]
     (when-let [target-asset-types (get assets/target-asset-type-mappings pipeline-entity-type)]
       (let [selected (:pipeline/target-asset-types @state)
             options (->> target-asset-types
                          (mapv (fn [at]
                                  {:value at
                                   :label (loc/tr at)})))
             error-text (sales-pipeline-target-constraint state)]
         [:div
          [labelled-input/with-actions
           {:tools (clipboard/copy-tool selected options)}
           [labelled-input/view
            {:editor-type :editor.type/multi-select
             :data-type   :data.type/keyword-multi
             :label       (or target-asset-label (loc/tr :pipeline/target-asset-types))
             :mandatory?  true
             :value       selected
             :options     options
             :error       error-text
             :on-change   (fn [new-value & _]
                            (swap! state assoc :pipeline/target-asset-types (vec new-value)))}]]
          (when error-text
            [:p {:class "text-xs text-danger-light-text"} error-text])]))
     (let [selected (ref-multi-value (:pipeline/teams @state))
           teams-schema (assoc pipeline-teams-schema
                               :field/option-filter-fn
                               (partial option-filters/team-with-module-access
                                        (case pipeline-entity-type
                                          :entity.type/oppo :module/dealflow
                                          :entity.type/fundraising-opportunity :module/fundraising
                                          :entity.type/sales-opportunity :module/sales-pipelines
                                          :entity.type/exit-opportunity :module/exit-process-management
                                          (logging/warn "Unknown pipeline type : " @state))))
           options (entity-options (fu/option-entities @state teams-schema))]
       [labelled-input/with-actions
        {:tools (clipboard/copy-tool selected options)}
        [labelled-input/view
         {:editor-type :editor.type/multi-select
          :data-type   :data.type/ref-multi
          :label       (loc/tr :grant-access-to-teams)
          :value       selected
          :options     options
          :on-change   (fn [new-value & _]
                         (swap! state assoc :pipeline/teams (vec new-value)))}]])
     (when funds?
       (let [options (entity-options @session/all-funds)
             selected-fund (:pipeline/fund @state)]
         [labelled-input/view
          {:editor-type :editor.type/select
           :label       (loc/tr :pipeline/fund)
           :value       (->> options
                             (cu/filter-by :value selected-fund)
                             (first))
           :options     options
           :on-change   (fn [selected-value]
                          (swap! state assoc :pipeline/fund selected-value))}]))
     (when (session/has-funds-access?)
       (let [selected (ref-multi-value (:pipeline/valid-funds @state))
             options (entity-options @session/all-funds)]
         [labelled-input/with-actions
          {:tools (clipboard/copy-tool selected options)}
          [labelled-input/view
           {:editor-type :editor.type/multi-select
            :data-type   :data.type/ref-multi
            :label       (loc/tr :pipeline/valid-funds)
            :value       selected
            :options     options
            :on-change   (fn [new-value & _]
                           (swap! state assoc :pipeline/valid-funds (vec new-value)))}]]))
     (when (and (session/froppo-creation-flow-feature?)
                (contains? #{:entity.type/fundraising-opportunity
                             :entity.type/sales-opportunity
                             :entity.type/exit-opportunity
                             :entity.type/oppo}
                           pipeline-entity-type))
       (let [allow? (:pipeline/allow-without-target? @state)]
         [:div
          {:class "flex items-center gap-2"}
          [:span
           (case pipeline-entity-type
             :entity.type/fundraising-opportunity
             (loc/tr :pipeline-config/allow-without-froppo-target)
             :entity.type/sales-opportunity
             (loc/tr :pipeline-config/allow-without-sales-customer)
             :entity.type/exit-opportunity
             (loc/tr :pipeline-config/allow-without-exit-buyer)
             :entity.type/oppo
             (loc/tr :pipeline-config/allow-without-deal-target)
             "")]
          [tooltip/view
           [:i.fal.fa-info-circle]
           (loc/tr :pipeline-config/allow-without-target-info)]
          [toggle/toggle
           {:checked?  allow?
            :on-change (fn [toggled?]
                         (swap! state assoc :pipeline/allow-without-target? toggled?))}]]))
     [:div
      {:class "flex items-center gap-2"}
      [:span (loc/tr :pipeline-archived)]
      [toggle/toggle
       {:checked?  (:pipeline/inactive? @state)
        :on-change (fn [toggled?]
                     (swap! state assoc :pipeline/inactive? toggled?))}]]]))

(defn footer-view
  [state duplicate-name? sales-target-constraint?]
  [:div
   {:class "ml-auto"}
   [modal/right-single-button
    nil
    (or duplicate-name?
        sales-target-constraint?
        (not (events/form-ready? state))
        (events/no-changes? state))
    #(events/save! state)
    (loc/tr :save)
    nil]])

(def title-by-entity-type
  {:entity.type/oppo                    (loc/tr :deal-pipeline-config)
   :entity.type/fundraising-opportunity (loc/tr :fundraising-pipeline-config)
   :entity.type/sales-opportunity       (loc/tr :sales-pipeline-config)
   :entity.type/exit-opportunity        (loc/tr :exit-pipeline-config)})

(defn view
  [{:keys [shown? close-modal! props state class-name topmost?]}]
  (let [{:keys [existing-pipelines target-asset-label pipeline-id]} props
        on-close! close-modal!]
    [processing-modal/render-modal
     {:state             state
      :shown?            (and shown? topmost?)
      :success-text      (loc/tr :success-pipeline-updated)
      :on-close!         on-close!
      :class-name        class-name
      :on-show!          (fn []
                           (reset! state (session/get-entity pipeline-id)))
      :default-title-fn  #(let [pipeline-entity-type (->> pipeline-id
                                                          session/get-entity
                                                          :pipeline/entity-type)]
                            (get title-by-entity-type pipeline-entity-type
                                 (loc/tr :pipeline-edit-modal/title)))
      :default-body-fn   (partial view-body state existing-pipelines target-asset-label)
      :default-footer-fn (partial footer-view
                                  state
                                  (not-unique-pipeline? existing-pipelines (:pipeline/name @state))
                                  (sales-pipeline-target-constraint state))}]))
